/**
 * CR107: Layout Studio Types
 * 
 * Type definitions for Deal Finder page layout configuration.
 */

// ================================================================
// BLOCK DEFINITIONS
// ================================================================

/**
 * CR500: All UI elements must be represented as blocks.
 * Adding a new block requires:
 * 1. Add to this type
 * 2. Add to BLOCK_REGISTRY
 * 3. Create BlockComponent
 * 4. Add to BLOCK_LABELS
 */
export type LayoutBlockId = 
  | "header"
  | "search_filters"
  | "sort_bar"
  | "deal_type_filter"   // CR228: Deal type filter pills block
  | "results_meta"       // CR500: "X deals found/available" display
  | "divider"            // CR500: Visual separator block
  | "results"
  | "ambiguous_deals"    // CR228: Ambiguous/unverified deals block
  | "empty_state"        // CR228: Empty state block
  | "featured_deals";

export type FilterControlId =
  | "search"
  | "category"
  | "deal_type"
  | "promo_merchant"
  | "reward_brand"
  | "clear_all";

export type SortOptionId =
  | "newest"
  | "best_value"
  | "expiring_soon"
  | "merchant_az"
  | "category_az";

// ================================================================
// BLOCK CONFIGS
// ================================================================

import type { HeaderTypographyConfig } from "./headerTypographyPresets";
/** CR130: Import DealCardUXConfig for card sizing/density/details mode */
import type { DealCardUXConfig } from "./dealCardUXTypes";
import { DEFAULT_DEAL_CARD_UX_CONFIG } from "./dealCardUXTypes";

export interface HeaderBlockConfig {
  showLogo: boolean;
  showTitle: boolean;
  showSubtitle: boolean;
  alignment: "left" | "center";
  logoSize: "sm" | "md" | "lg";
  /** CR124: Custom title text (defaults to "Deal Finder") */
  titleText?: string;
  /** CR124: Custom subtitle text (defaults to "Find the best gift card deals") */
  subtitleText?: string;
  /** CR124: Asset Studio asset ID for logo (preferred) */
  logoAssetId?: string;
  /** CR124: Fallback logo URL if not using Asset Studio */
  logoUrl?: string;
  /** CR126: Header typography preset configuration */
  headerTypography?: HeaderTypographyConfig;
  /** Legacy - use titleText instead */
  titleOverride?: string;
  /** Legacy - use subtitleText instead */
  subtitleOverride?: string;
  bannerText?: string;
}

export interface FilterConfig {
  defaultValue?: string | string[];
  multiSelect: boolean;
  collapsed: boolean;
  sticky: boolean;
}

/**
 * CR500: SortBarBlock is now fully config-driven.
 * The sort dropdown visibility is controlled by showSortDropdown.
 */
export interface SortBarConfig {
  showLabel: boolean;
  /** CR500: Show the sort dropdown selector */
  showSortDropdown?: boolean;
  /** CR500: Show the sort icon next to dropdown */
  showSortIcon?: boolean;
}

/**
 * CR500: ResultsMetaBlock config
 * CR283: Extended with typography controls and template support.
 * Controls the "X deals found/available" display.
 */
export interface ResultsMetaBlockConfig {
  /** Show the deal count */
  showCount: boolean;
  /** Display style for count */
  countStyle: "compact" | "headline";
  /** Label text (e.g., "Deals Available", "Results") */
  labelText: string;
  /** Show "found" text variation when filtering */
  showFoundText: boolean;
  /** CR283: Text template with {count} and {dealWord} tokens */
  template?: string;
  /** CR283: Font family ID from fontRegistry */
  fontFamily?: string;
  /** CR283: Font size in pixels */
  fontSize?: number;
  /** CR283: Text alignment */
  align?: "left" | "center" | "right";
}

/**
 * CR500: DividerBlock config
 * Visual separator between sections.
 */
export interface DividerBlockConfig {
  /** Divider style variant */
  variant: "line" | "space";
  /** Spacing size */
  spacing: "sm" | "md" | "lg";
}

/**
 * CR315: Cards View configuration
 * Controls grid/list view toggle and customer preferences.
 */
export interface CardsViewConfig {
  /** Default view mode */
  defaultView: "grid" | "list";
  /** Allow customers to toggle between grid/list */
  customerToggleEnabled: boolean;
  /** Which views are enabled */
  viewsEnabled: ("grid" | "list")[];
  /** Persist customer preference in localStorage */
  persistCustomerPreference: boolean;
}

/**
 * CR315: Search filter configuration
 * Controls search scope and behavior.
 */
/**
 * CR316: Search suggestions configuration
 */
export interface SearchSuggestionsConfig {
  /** Enable search suggestions */
  enabled: boolean;
  /** Minimum characters before suggestions appear */
  minChars: number;
  /** Maximum number of suggestions to show */
  maxItems: number;
  /** Match mode for suggestions */
  matchMode: "starts_with_then_contains" | "contains" | "starts_with";
  /** Show type label (Merchant, Brand, etc.) */
  showTypeLabel: boolean;
  /** Show result counts */
  showCounts: boolean;
  /** Which suggestion types are enabled */
  typesEnabled: {
    merchant: boolean;
    brand: boolean;
    category: boolean;
    title: boolean;
  };
  /** Behavior when suggestion is selected */
  behavior: "apply_search_text" | "apply_filter";
  /** Dedupe identical suggestions across types */
  dedupe: boolean;
}

/**
 * CR316: Recent searches configuration
 */
export interface RecentSearchesConfig {
  /** Enable recent searches */
  enabled: boolean;
  /** Maximum number of recent items to store */
  maxItems: number;
  /** Show recent searches when search input is empty */
  showWhenEmpty: boolean;
  /** Show recent searches when input is focused */
  showWhenFocused: boolean;
  /** Show "Clear All" action */
  showClearAll: boolean;
}

export interface SearchFilterConfig {
  /** Placeholder text for search input */
  placeholderText: string;
  /** Debounce delay in ms */
  debounceMs: number;
  /** Minimum characters before search triggers */
  minChars: number;
  /** Search scope - which fields to search */
  scope: "card_visible" | "title_only" | "title_merchant";
  /** Fields to search when scope is card_visible */
  scopeFields: ("title" | "condition" | "reward" | "merchant")[];
  /** CR316: Search suggestions configuration */
  suggestions?: SearchSuggestionsConfig;
  /** CR316: Recent searches configuration */
  recentSearches?: RecentSearchesConfig;
}

export interface ResultsBlockConfig {
  layoutType: "grid" | "list";
  gridColumns: {
    desktop: number;
    tablet: number;
    mobile: number;
  };
  density: "compact" | "comfortable";
  pagination: "pages" | "infinite";
  /** CR315: Page size default is 50 */
  pageSize: number;
  emptyStateText?: string;
  /** CR315: Cards view configuration */
  cardsView?: CardsViewConfig;
}

// ================================================================
// LAYOUT ITEMS
// ================================================================

export interface LayoutBlock {
  id: LayoutBlockId;
  enabled: boolean;
  order: number;
  config?: HeaderBlockConfig | SortBarConfig | ResultsBlockConfig | ResultsMetaBlockConfig | DividerBlockConfig | Record<string, unknown>;
}

export interface FilterControl {
  id: FilterControlId;
  enabled: boolean;
  order: number;
  /** CR315: Search filter has its own config type */
  config?: FilterConfig | SearchFilterConfig;
}

export interface SortOption {
  id: SortOptionId;
  enabled: boolean;
  order: number;
}

// ================================================================
// FULL LAYOUT JSON
// ================================================================

/** CR109: Typography settings for Layout Studio */
export interface LayoutTypographyConfig {
  /** UI font ID from fontRegistry */
  uiFontId?: string;
  /** Title/heading font ID from fontRegistry */
  titleFontId?: string;
}

export type LayoutEditorMode = "flow" | "canvas";

export type CanvasArtboardId = "desktop" | "tablet" | "mobile";

export interface CanvasNodeRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LayoutCanvasNode {
  id: string;
  blockId: LayoutBlockId;
  rect: CanvasNodeRect;
  z: number;
  visible: boolean;
  locked: boolean;
}

export type CanvasBackgroundLayerKind = "solid" | "gradient" | "asset";

export interface LayoutCanvasBackgroundLayer {
  id: string;
  kind: CanvasBackgroundLayerKind;
  visible: boolean;
  z: number;
  opacity: number;
  solidColor?: string;
  gradient?: {
    color1: string;
    color2: string;
    angle: number;
  };
  asset?: {
    assetId: string;
    fit: "cover" | "contain" | "repeat";
    tint?: string | null;
  };
}

export interface LayoutCanvasArtboard {
  width: number;
  minHeight: number;
  nodes: LayoutCanvasNode[];
}

export interface LayoutCanvasConfig {
  activeArtboard: CanvasArtboardId;
  artboards: Record<CanvasArtboardId, LayoutCanvasArtboard>;
  backgroundLayers: LayoutCanvasBackgroundLayer[];
}

export interface LayoutJson {
  /** Editor mode: flow (legacy stack) or canvas (popup editor) */
  editorMode?: LayoutEditorMode;
  blocks: LayoutBlock[];
  filters: FilterControl[];
  sortOptions: SortOption[];
  defaultSort: SortOptionId;
  resultsConfig: ResultsBlockConfig;
  /** CR109: Typography configuration for page UI */
  typography?: LayoutTypographyConfig;
  /** CR130: Deal card UX configuration (sizing, density, details mode) */
  dealCardUX?: DealCardUXConfig;
  /** Canvas composition config for popup editor mode */
  canvas?: LayoutCanvasConfig;
}

// ================================================================
// DATABASE TYPES
// ================================================================

export type PresetStatus = "draft" | "published" | "archived";
export type ActivationEnvironment = "staging" | "publish";
export type AuditAction = "activate" | "rollback";

export interface LayoutPreset {
  id: string;
  name: string;
  description: string | null;
  layout_json: LayoutJson;
  status: PresetStatus;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface LayoutActivation {
  environment: ActivationEnvironment;
  active_preset_id: string | null;
  updated_at: string;
  updated_by: string | null;
  preset?: {
    id: string;
    name: string;
    status: PresetStatus;
  } | null;
}

export interface LayoutActivationAudit {
  id: string;
  environment: ActivationEnvironment;
  action: AuditAction;
  old_preset_id: string | null;
  new_preset_id: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  old_preset?: { id: string; name: string } | null;
  new_preset?: { id: string; name: string } | null;
}

// ================================================================
// DEFAULTS
// ================================================================

import { DEFAULT_HEADER_TYPOGRAPHY } from "./headerTypographyPresets";

export const DEFAULT_HEADER_CONFIG: HeaderBlockConfig = {
  showLogo: true,
  showTitle: true,
  showSubtitle: true,
  alignment: "left",
  logoSize: "md",
  // CR124: defaults
  titleText: "Deal Finder",
  subtitleText: "Find the best gift card deals",
  logoAssetId: undefined,
  logoUrl: undefined,
  // CR126: default typography preset
  headerTypography: DEFAULT_HEADER_TYPOGRAPHY,
};

export const DEFAULT_FILTER_CONFIG: FilterConfig = {
  multiSelect: false,
  collapsed: false,
  sticky: false,
};

/** CR500: Default SortBar config */
export const DEFAULT_SORT_BAR_CONFIG: SortBarConfig = {
  showLabel: true,
  showSortDropdown: true,
  showSortIcon: true,
};

/** CR500: Default ResultsMeta config, CR283: Added typography defaults */
export const DEFAULT_RESULTS_META_CONFIG: ResultsMetaBlockConfig = {
  showCount: true,
  countStyle: "headline",
  labelText: "Deals Available",
  showFoundText: true,
  template: "Showing {count} {dealWord}",
  fontFamily: undefined, // inherit from theme
  fontSize: 14,
  align: "left",
};

/** CR500: Default Divider config */
export const DEFAULT_DIVIDER_CONFIG: DividerBlockConfig = {
  variant: "line",
  spacing: "md",
};

/** CR315: Default Cards View config */
export const DEFAULT_CARDS_VIEW_CONFIG: CardsViewConfig = {
  defaultView: "grid",
  customerToggleEnabled: true,
  viewsEnabled: ["grid", "list"],
  persistCustomerPreference: true,
};

/** CR316: Default Search Suggestions config */
export const DEFAULT_SEARCH_SUGGESTIONS_CONFIG: SearchSuggestionsConfig = {
  enabled: true,
  minChars: 2,
  maxItems: 8,
  matchMode: "starts_with_then_contains",
  showTypeLabel: true,
  showCounts: false,
  typesEnabled: {
    merchant: true,
    brand: true,
    category: true,
    title: false,
  },
  behavior: "apply_search_text",
  dedupe: true,
};

/** CR316: Default Recent Searches config */
export const DEFAULT_RECENT_SEARCHES_CONFIG: RecentSearchesConfig = {
  enabled: true,
  maxItems: 6,
  showWhenEmpty: true,
  showWhenFocused: true,
  showClearAll: true,
};

/** CR315: Default Search Filter config (CR316: extended with suggestions + recents) */
export const DEFAULT_SEARCH_FILTER_CONFIG: SearchFilterConfig = {
  placeholderText: "Search deals",
  debounceMs: 200,
  minChars: 1,
  scope: "card_visible",
  scopeFields: ["title", "condition", "reward", "merchant"],
  suggestions: DEFAULT_SEARCH_SUGGESTIONS_CONFIG,
  recentSearches: DEFAULT_RECENT_SEARCHES_CONFIG,
};

/** CR315: Default Results config - page size 50 */
export const DEFAULT_RESULTS_CONFIG: ResultsBlockConfig = {
  layoutType: "grid",
  gridColumns: { desktop: 3, tablet: 2, mobile: 1 },
  density: "comfortable",
  pagination: "pages",
  pageSize: 50,  // CR315: Updated from 12 to 50
  cardsView: DEFAULT_CARDS_VIEW_CONFIG,
};

/**
 * CR500: Default layout with all blocks represented.
 * Every UI element on the public page MUST be a block here.
 */
export const DEFAULT_LAYOUT_JSON: LayoutJson = {
  editorMode: "flow",
  blocks: [
    { id: "header", enabled: true, order: 0, config: DEFAULT_HEADER_CONFIG },
    { id: "search_filters", enabled: true, order: 1 },
    { id: "deal_type_filter", enabled: true, order: 2 },
    { id: "divider", enabled: false, order: 3, config: DEFAULT_DIVIDER_CONFIG },  // CR500: Visual separator
    { id: "sort_bar", enabled: true, order: 4, config: DEFAULT_SORT_BAR_CONFIG },  // CR500: Now enabled by default
    { id: "results_meta", enabled: true, order: 5, config: DEFAULT_RESULTS_META_CONFIG },  // CR500: "X Deals Available"
    { id: "empty_state", enabled: true, order: 6 },
    { id: "results", enabled: true, order: 7, config: DEFAULT_RESULTS_CONFIG },
    { id: "ambiguous_deals", enabled: true, order: 8 },
    { id: "featured_deals", enabled: false, order: 9 },
  ],
  filters: [
    { id: "search", enabled: true, order: 0, config: DEFAULT_SEARCH_FILTER_CONFIG },  // CR315: Search config
    { id: "category", enabled: true, order: 1, config: { ...DEFAULT_FILTER_CONFIG, multiSelect: true } },
    { id: "deal_type", enabled: true, order: 2, config: { ...DEFAULT_FILTER_CONFIG, multiSelect: true } },
    { id: "promo_merchant", enabled: false, order: 3 },
    { id: "reward_brand", enabled: false, order: 4 },
    { id: "clear_all", enabled: true, order: 5 },
  ],
  sortOptions: [
    { id: "newest", enabled: true, order: 0 },
    { id: "best_value", enabled: true, order: 1 },
    { id: "expiring_soon", enabled: true, order: 2 },
    { id: "merchant_az", enabled: true, order: 3 },
    { id: "category_az", enabled: false, order: 4 },
  ],
  defaultSort: "newest",
  resultsConfig: DEFAULT_RESULTS_CONFIG,
  // CR109: Default typography (uses Inter)
  typography: {
    uiFontId: "inter",
    titleFontId: "inter",
  },
  // CR130: Default deal card UX config
  dealCardUX: DEFAULT_DEAL_CARD_UX_CONFIG,
  canvas: {
    activeArtboard: "desktop",
    artboards: {
      desktop: {
        width: 1200,
        minHeight: 1800,
        nodes: [],
      },
      tablet: {
        width: 900,
        minHeight: 1600,
        nodes: [],
      },
      mobile: {
        width: 390,
        minHeight: 1800,
        nodes: [],
      },
    },
    backgroundLayers: [],
  },
};

// ================================================================
// UI LABELS
// ================================================================

/**
 * CR500: Every block MUST have a label for the Layout Studio editor.
 */
export const BLOCK_LABELS: Record<LayoutBlockId, string> = {
  header: "Header",
  search_filters: "Search & Filters",
  sort_bar: "Sort Controls",
  deal_type_filter: "Deal Type Filter Pills",
  results_meta: "Results Count / Header",  // CR500
  divider: "Divider / Spacer",             // CR500
  results: "Results Grid/List",
  ambiguous_deals: "Ambiguous/Unverified Deals",
  empty_state: "Empty State",
  featured_deals: "Featured Deals",
};

export const FILTER_LABELS: Record<FilterControlId, string> = {
  search: "Search Bar",
  category: "Category Filter",
  deal_type: "Deal Type Filter",
  promo_merchant: "Promo Merchant Filter",
  reward_brand: "Reward Brand Filter",
  clear_all: "Clear All Button",
};

export const SORT_LABELS: Record<SortOptionId, string> = {
  newest: "Newest",
  best_value: "Best Value",
  expiring_soon: "Expiring Soon",
  merchant_az: "Merchant A–Z",
  category_az: "Category A–Z",
};
